/**
 * Iniciar uma execução: pede o identificador (obrigatório ou não, conforme a
 * tarefa — campo `exige_identificador`, nunca regra no código) e trata o
 * "este lote já está aberto" oferecendo abrir a existente.
 *
 * Exige conexão: só o servidor garante que não há dois "522 - … - L2" abertos.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import { ehFalhaDeRede, iniciarExecucao } from "../../../services/areaPcp";
import { novaChave } from "../dominio/editor";
import { passoDeTotal } from "../dominio/formatos";
import { useAreaPcp } from "../offline/AreaPcpContext";
import { Aviso, BotaoPrimario, BotaoSecundario, Campo, Mono } from "../styles";
import Folha from "./Folha";

export const ROTA_EXECUCAO = (id) => `/legado/area-pcp/execucoes/${id}`;

export default function IniciarExecucao({ tarefa, aberta, onFechar }) {
  const { request } = useAuth();
  const { atualizarExecucao, dispositivo } = useAreaPcp();
  const navigate = useNavigate();
  const [identificador, setIdentificador] = useState("");
  const [erro, setErro] = useState("");
  const [existente, setExistente] = useState(null);
  const [enviando, setEnviando] = useState(false);
  // Mesmo id em toques repetidos: o backend devolve a mesma execução.
  const [idDaExecucao, setIdDaExecucao] = useState(novaChave);

  useEffect(() => {
    if (!aberta) return;
    setIdentificador("");
    setErro("");
    setExistente(null);
    setIdDaExecucao(novaChave());
  }, [aberta]);

  if (!tarefa) return null;
  const rotulo = tarefa.rotulo_identificador || "Identificação";
  const obrigatorio = Boolean(tarefa.exige_identificador);

  const comecar = async (e) => {
    e?.preventDefault();
    if (obrigatorio && !identificador.trim()) {
      setErro(`Informe ${rotulo.toLowerCase()} para começar.`);
      return;
    }
    setEnviando(true);
    setErro("");
    try {
      const execucao = await iniciarExecucao(request, {
        id: idDaExecucao, tarefa_id: tarefa.id, identificador: identificador.trim(), dispositivo,
      });
      atualizarExecucao(execucao);
      onFechar?.();
      navigate(ROTA_EXECUCAO(execucao.id));
    } catch (falha) {
      if (falha.status === 409 && falha.payload?.existente) setExistente(falha.payload.existente);
      else if (ehFalhaDeRede(falha)) setErro("Sem conexão. Para começar uma execução é preciso internet; as já abertas continuam funcionando.");
      else setErro(falha.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Folha
      aberta={aberta}
      onFechar={onFechar}
      titulo="Nova execução"
      subtitulo={`${tarefa.titulo} · ${tarefa.n_passos} passos · começa no passo 1`}
      acoes={
        existente ? (
          <>
            <BotaoSecundario type="button" onClick={() => setExistente(null)}>
              Usar outro nome
            </BotaoSecundario>
            <BotaoPrimario type="button" onClick={() => { onFechar?.(); navigate(ROTA_EXECUCAO(existente.id)); }}>
              Abrir a existente
            </BotaoPrimario>
          </>
        ) : (
          <>
            <BotaoSecundario type="button" onClick={onFechar}>
              Cancelar
            </BotaoSecundario>
            <BotaoPrimario type="submit" form="form-iniciar-execucao" disabled={enviando}>
              {enviando ? "Começando…" : "Começar"}
            </BotaoPrimario>
          </>
        )
      }
    >
      <form id="form-iniciar-execucao" onSubmit={comecar}>
        <Campo>
          <span>
            {rotulo}
            {obrigatorio ? " *" : " (opcional)"}
          </span>
          <input
            value={identificador}
            onChange={(e) => {
              setIdentificador(e.target.value);
              setExistente(null);
            }}
            placeholder={tarefa.exemplo_identificador ? `Ex.: ${tarefa.exemplo_identificador}` : ""}
            style={{ fontFamily: "var(--font-mono)" }}
            aria-invalid={Boolean(erro)}
            autoComplete="off"
          />
          {tarefa.formato_identificador ? <span className="dica">Formato: {tarefa.formato_identificador}</span> : null}
        </Campo>
      </form>
      {erro ? <Aviso $tom="danger" role="alert">{erro}</Aviso> : null}
      {existente ? (
        <Aviso $tom="warning" role="alert">
          <div>
            <strong>
              <Mono>{existente.identificador}</Mono> já está aberto
            </strong>
            <div>
              {existente.status === "pausada" ? "Pausado" : "Em andamento"} · {passoDeTotal(existente)}
              {existente.passo_atual?.titulo_curto ? ` · ${existente.passo_atual.titulo_curto}` : ""}. Iniciado por{" "}
              {existente.iniciada_por || "—"}.
            </div>
          </div>
        </Aviso>
      ) : null}
    </Folha>
  );
}
